import React, { useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CommentIcon from '@mui/icons-material/Comment'
import DeleteIcon from '@mui/icons-material/Delete'
import SendIcon from '@mui/icons-material/Send'
import {
  Avatar,
  Box,
  ClickAwayListener,
  Divider,
  Grow,
  IconButton,
  Paper,
  Popper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'

import { apiZodios } from '~api/apiZodios'
import { lightTheme } from '~createTheme'
import { RootState } from '~redux/rootReducer'
import { CommentThread } from '~types'

import { ASSET_URLS } from '../../constants'

interface CommentComponentProps {
  commentThread: CommentThread
  readOnly?: boolean
  onCommentUpdate?: () => void
}

const CommentComponent: React.FC<CommentComponentProps> = ({
  commentThread,
  readOnly = true,
  onCommentUpdate,
}) => {
  const [open, setOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [newComment, setNewComment] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const user = useSelector((state: RootState) => state?.auth?.user)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleToggle = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget)
    setOpen((prevOpen) => !prevOpen)
    setError(null)
  }

  const handleClose = (): void => {
    setOpen(false)
    setError(null)
  }

  const handleAddComment = async (): Promise<void> => {
    if (!newComment.trim() || !user?.id) return

    try {
      setIsSubmitting(true)
      setError(null)

      await apiZodios.cases_addComment({
        user_id: user.id,
        content: newComment,
        author_id: user.identity_id,
        form_id: commentThread.form,
        input_id: commentThread.input || undefined,
        thread_id: commentThread.id,
      })

      setNewComment('')
      onCommentUpdate?.()
    } catch (err) {
      console.error('Failed to add comment:', err)
      setError('Failed to add comment')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteComment = async (commentId: string): Promise<void> => {
    if (!user?.id) return

    try {
      await apiZodios.cases_deleteComment({
        user_id: user.id,
        comment_id: commentId,
      })

      onCommentUpdate?.()
    } catch (err) {
      console.error('Failed to delete comment:', err)
      setError('Failed to delete comment')
    }
  }

  const handleResolveThread = async (): Promise<void> => {
    if (!user?.id) return

    try {
      await apiZodios.cases_resolveThread({
        user_id: user.id,
        thread_id: commentThread.id,
        is_resolved: true,
      })

      onCommentUpdate?.()
      handleClose()
    } catch (err) {
      console.error('Failed to resolve thread:', err)
      setError('Failed to resolve thread')
    }
  }

  const commentCount = commentThread?.comments?.length || 0
  if (!commentCount) return null

  return (
    <>
      <IconButton
        size='small'
        onClick={handleToggle}
        sx={{
          color: commentThread.resolved
            ? lightTheme.palette.grey[500]
            : lightTheme.palette.primary.main,
          '&:hover': {
            backgroundColor: lightTheme.palette.background.paper,
          },
          position: 'relative',
        }}
      >
        <CommentIcon fontSize='small' />
        {commentCount > 1 && (
          <Typography
            variant='caption'
            component='span'
            sx={{
              position: 'absolute',
              top: -2,
              right: -2,
              backgroundColor: commentThread.resolved
                ? lightTheme.palette.grey[500]
                : lightTheme.palette.primary.main,
              color: 'white',
              borderRadius: '50%',
              width: 16,
              height: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.7rem',
            }}
          >
            {commentCount}
          </Typography>
        )}
      </IconButton>

      <Popper
        open={open}
        anchorEl={anchorEl}
        transition
        placement='right-start'
        sx={{ zIndex: 1300 }}
      >
        {({ TransitionProps }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: 'left top',
            }}
          >
            <Paper
              elevation={3}
              sx={{
                width: 320,
                maxHeight: 400,
                overflowY: 'auto',
                p: 2,
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <Box>
                  {commentThread.comments.map((comment, index) => (
                    <Box key={comment.id} sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          mb: 0.5,
                          width: '100%',
                        }}
                      >
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Avatar
                            src={comment.commenter_profile || ASSET_URLS.icon}
                            alt={comment.commenter_name || 'Polimorphic AI'}
                            sx={{
                              width: 24,
                              height: 24,
                              '& img': {
                                objectFit: 'contain',
                                width: '80%',
                                height: '80%',
                              },
                            }}
                          />
                          <Box>
                            <Typography variant='body2' fontWeight='bold'>
                              {comment.commenter_name || 'Polimorphic AI'}
                            </Typography>
                            {comment.time && (
                              <Typography variant='caption' color='text.secondary'>
                                {new Date(comment.time).toLocaleString([], {
                                  dateStyle: 'short',
                                  timeStyle: 'short',
                                })}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        {index === 0 && !commentThread.resolved && !readOnly && (
                          <Tooltip title='Mark thread as resolved'>
                            <IconButton sx={{ mb: 3 }} size='small' onClick={handleResolveThread}>
                              <CheckCircleIcon fontSize='small' />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', ml: 4 }}>
                        <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap', flex: 1 }}>
                          {comment.comment}
                        </Typography>
                        {!readOnly && user?.identity_id === comment.commenter && (
                          <IconButton
                            size='small'
                            onClick={() => handleDeleteComment(comment.id)}
                            sx={{ ml: 1, mt: -0.5 }}
                          >
                            <DeleteIcon fontSize='small' />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  ))}

                  {!readOnly && !commentThread.resolved && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      {error && (
                        <Typography
                          variant='caption'
                          color='error'
                          sx={{ mb: 1, display: 'block' }}
                        >
                          {error}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                          inputRef={inputRef}
                          fullWidth
                          size='small'
                          placeholder='Add a comment...'
                          multiline
                          minRows={1}
                          maxRows={3}
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          disabled={isSubmitting}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && e.ctrlKey) {
                              handleAddComment()
                            }
                          }}
                        />
                        <Tooltip title='Send comment'>
                          <IconButton
                            size='small'
                            onClick={handleAddComment}
                            disabled={!newComment.trim() || isSubmitting}
                          >
                            <SendIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </>
                  )}
                </Box>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  )
}

export default CommentComponent
